import axios from 'axios';
import { DateTime } from 'luxon';
import { Tag } from './tag.api';
import FileUtils from '../../utils/file.utils';
import Formatter from '../../utils/formatters';
import { DocumentClassificationAndType } from './documentManagement.api';
import TagFormat from '../../utils/tagFormat';
import { WarehouseStatusCode } from '../../utils/warehouse.utils';
import { Paginated } from '../../components/BaseTable/partials/PaginationJp/mannual';
import { Folder } from './folder.api';
import { ControlCodeItem } from '../../utils/controlCode';
import { ERROR_MESSAGES } from '../../utils/messages';
import { ApiError } from './index';

export enum Order {
  ASC = 0,
  DESC = 1,
}

export type ContractFlag = 1 | 0;

export interface DocumentClassificationOption {
  documentClassification: DocumentClassificationAndType;
  type: DocumentClassificationAndType['types'][number];
}

export interface DuplicateTag {
  tagLabel?: string,
  value?: string,
}

export interface RelationalDocument {
  ccControlNumber?: number,
  documentId?: number,
  documentName?: string,
  documentType?: string,
  barcodePrinting?: string,
  itemCode?: string,
  folderName?: string,
  duplicateTagList?: DuplicateTag[],
}

export interface Document {
  ccControlNumber: number
  id: number
  name: string
  fileName?: string
  fileSize?: number
  fileId?: number
  typeId?: number
  typeName?: string
  tagList?: { label: string, value: string, }[]
  attachmentFileCount: number
  relationalDocumentCount?: number
  relationalDocumentList?: RelationalDocument[]
  warehouseStatus?: string
  warehouseStatusCode?: string
  paperFlg?: ContractFlag
  electronicFlg?: ContractFlag
  registUser: string
  registDate: Date
  updateUser: string
  updateDate: Date
  itemCode?: string
  barcodePrinting: string
  requiredTagInputFlg?: number
  folderId: number
  folderName: string
  authorityList: ControlCodeItem[]
  cartAddedFlg?: number
  searchResult?: string
  previousPdfCount: number
}

export interface DocumentDetail {
  ccControlNumber: number,
  id: number,
  documents: {
    dataId: number,
    versionNo: number,
    name: string,
    fileName: string,
    fileSize: number,
    documentFileOcrFlg: boolean,
    fileId: number,
    classification: number,
    typeId: number,
    typeName: string,
    memo: string,
    attachmentFileList: {
      id: number,
      fileName: string,
    }[],
    tags: {
      tagLabel: string,
      format: TagFormat,
      formatCheck?: number,
      value: string,
      required: boolean,
    }[],
    relationalDocuments: {
      ccControlNumber: number,
      id: number,
      name: string,
      type: string,
      duplicateTags: {
        label: string,
        value: string,
      }[],
    }[],
  }[],
  paperFlg: ContractFlag,
  electronicFlg: ContractFlag,
  barcodePrinting: string,
  barcode: string,
  warehouseStatus: string | null,
  warehouseStatusCode: string | null,
  itemCode: string,
  scheduledDate: Date | null,
  barcodeChangeable: number,
  requestHistory: {
    date: string,
    type: string,
    user: string,
  }[],
  versionUpdateHistory: {
    logId: number | null,
    registDate: string,
    requestUser: string,
    updateType: string,
  }[],
  folder: Folder,
  registUser: string,
  registDate: Date,
  updateUser: string,
  updateDate: Date,
}
export interface DocumentFolder {
  documentList: {
    documentId: number,
    documentName: string,
    documentFileName?: string,
    documentFileSize?: number,
    documentFileId?: number,
    documentTypeId?: number,
    documentTypeName?: string,
  }[],
  tagList?: {
    tagLabel?: string,
    tagValue?: string,
    relationalDocumentCount?: number,
  }[],
  relationalDocumentList?: RelationalDocument[],
  warehouseStatus?: string,
  paperFlg?: ContractFlag,
  electronicFlg?: ContractFlag,
  registUser: string,
  registDate: string,
  updateUser: string,
  itemCode?: string,
  total: number,
  page: number,
}

export interface DocumentPagination extends Paginated {
  highlightWordList: string[],
  documents: Document[],
  searchKeyword?: string,
  searchKeywordList?: string[],
  sharedToken: string,
}

export interface DocumentOcrPagination extends Paginated {
  ocrHighlightWordList: string[],
  documents: Document[],
  searchKeyword: string,
  searchKeywordList: string[],
  sharedToken: string,
}

export interface DocumentSearchFolderForm {
  folder: Folder,
  page: number,
  sortList?: SortList[],
}

export interface SortList {
  item: string,
  order: Order,
}

export interface DocumentRegisterForm {
  internalId: string,
  documentName: string,
  documentTypeId: number,
  documentTypeName?: string,
  barcodePrinting?: string,
  documentClassification?: number | null,
  folderId?: number,
  documentFileName?: string,
  documentFile?: File,
  paperFlg?: ContractFlag,
  electronicFlg?: ContractFlag,
  memo?: string,
  tagList: {
    tagLabel: string,
    format: TagFormat,
    formatCheck?: number,
    required: boolean,
    value: string,
    isValidValue?: boolean,
    isDuplicated?: boolean,
  }[],
  relationalDocumentList: Document[],
  attachmentFileList: {
    attachmentFile?: File,
    attachmentFileName?: string,
    attachmentFileSize?: number
    attachmentFileNameError?: string,
  }[],
  isFormBodySizeTooLarge?: boolean,
  documentFileError?: string,
}

export interface DocumentGetDetailForm {
  id: number,
}

export interface TagListUrl {
  label: string,
  condition: string,
  value1: string,
  value2?: string,
}

export interface SearchUrlForm {
  tagList?: TagListUrl[][];
  [key: string]: any;
}

export interface CcControlNumber {
  ccControlNumber: number,
}

export type TagQueryOperator = 'AND' | 'OR';

export interface TagCondition {
  tagSettingId?: number;
  label?: string;
  condition?: string;
  value1?: string;
  value2?: string;
}

export interface TagQuery {
  operator: TagQueryOperator,
  children: (TagCondition | TagQuery)[],
}

export interface DocumentSearchDetailForm {
  ccControlNumber?: string,
  simpleString?: string,
  pdfSimpleString?: string,
  name?: string,
  fileName?: string,
  barcodePrinting?: string,
  itemCode?: string,
  documentTypeId?: number,
  registDate_From?: DateTime,
  registDate_To?: DateTime,
  updateDate_From?: DateTime,
  updateDate_To?: DateTime,
  warehouseStatus?: WarehouseStatusCode,
  includePdf?: number,
  includeAttachment?: number,
  electronicContract?: ContractFlag,
  existPaper?: ContractFlag,
  memo?: string,
  ccControlNumberList?: CcControlNumber[],
  tagList?: TagCondition[][],
  tagQuery?: TagQuery,
  searchRange?: number,
  page?: number,
  sortList?: {
    item: string,
    order: Order,
  }[],
  bulkTagChange?: number,
}

export interface DocumentOcrSearchDetailForm {
  ccControlNumber?: string,
  pdfSimpleString?: string,
  name?: string,
  fileName?: string,
  barcodePrinting?: string,
  itemCode?: string,
  documentTypeId?: number,
  registDate_From?: DateTime,
  registDate_To?: DateTime,
  updateDate_From?: DateTime,
  updateDate_To?: DateTime,
  warehouseStatus?: WarehouseStatusCode,
  includePdf?: number,
  includeAttachment?: number,
  electronicContract?: ContractFlag,
  existPaper?: ContractFlag,
  memo?: string,
  ccControlNumberList?: CcControlNumber[],
  tagList?: TagCondition[][],
  tagQuery?: TagQuery,
  searchRange?: number,
  page?: number,
  sortList?: {
    item: string,
    order: Order,
  }[],
  bulkTagChange?: number,
}

export interface GetPdfForm {
  documentId: number,
  fileName: string,
}

export interface DownloadFileForm {
  documentDataId: number,
  fileName?: string,
}
export interface DownloadAttachmentForm {
  attachmentId: number,
  fileName?: string,
}

export interface BatchPdfDownloadNameRules {
  idPosition: 'start' | 'end';
  nameItems: {
    itemType: number,
    itemValue: string | null,
  }[]
}

export interface BatchPdfDownloadForm {
  documentIdList: { documentId: number }[],
  isPdfDownload: number,
  isAttachmentDownload: number,
  incOldVerFlg: number,
  nameRules?: BatchPdfDownloadNameRules,
}

export interface DocumentUpdateForm {
  id: number,
  name: string,
  file?: File,
  fileName?: string,
  fileSize?: number,
  fileId?: number,
  fileError?: string,
  fileNameError?: string,
  classification?: number,
  typeId?: number,
  typeName?: string,
  paperFlg?: ContractFlag,
  electronicFlg?: ContractFlag,
  barcodePrinting?: string,
  tagList: { tagLabel: string, value: string, format: TagFormat, formatCheck?: number, required: boolean, isValidValue?: boolean, isDuplicated?: boolean }[],
  memo?: string,
  relationalDocumentList?: { ccControlNumber?: number, id?: number, name?: string, type?: string, duplicateTags?: { label: string; value: string; }[] }[],
  attachmentFileList: { id?: number, file?: File, fileName?: string, fileSize?: number, deleted?: boolean, fileNameError?: string }[],
}

export interface DocumentRevisionForm {
  id: number,
  name: string,
  file?: File,
  fileName?: string,
  fileSize?: number,
  fileId?: number,
  classification?: number,
  typeId?: number,
  typeName?: string,
  paperFlg?: ContractFlag,
  electronicFlg?: ContractFlag,
  barcodePrinting?: string,
  tagList: { tagLabel: string, value: string, format: TagFormat, formatCheck?: number }[],
  memo?: string,
  relationalDocumentList?: { id?: number, }[],
  attachmentFileList: { id?: number, file?: File, fileName?: string, fileSize?: number }[],
}
export interface DocumentDeleteForm {
  id: number,
}

export interface DocumentDeleteRevisionForm {
  id: number,
  versionId: number,
}

export interface DocType {
  id: number;
  name: string;
  displayOrder: number;
}

export interface DocumentType {
  docType: DocType;
  documentClassification: DocumentClassificationAndType;
  name: string;
}

export interface DocsToRegisterFromCsv {
  docName: string,
  electronicFlg: string,
  paperFlg: string,
  memo?: string,
  barcodePrinting?: string,
  pdfFileName: string
  tags: Tag[],
  rowNo?: number,
}

export interface DocumentsFromCsv {
  ContractFlag?: number;
  documentType?: DocumentType;
  hasScreen?: number;
  docsToRegister: DocsToRegisterFromCsv[];
  documentClassificationOption?: DocumentClassificationOption;
}

export interface MoveDocumentFolderForm {
  documentId: number,
  folderId: number,
}

function convertToPagination(data: any, folder?: Folder): DocumentPagination {
  return {
    pageLimit: data.pageLimit,
    page: data.page,
    total: data.total,
    highlightWordList: data.highlightWordList ? data.highlightWordList.map((item: { highlightWord: string }) => item.highlightWord) : [],
    documents: data.documentList.map((item: {
      ccControlNumber: number,
      documentId: number,
      documentName: string,
      documentFileName: string,
      documentFileSize: number,
      documentFileId: number,
      documentTypeId: number,
      documentTypeName: string,
      tagList: { tagLabel: string, tagValue: string }[],
      attachmentFileCount: number,
      relationalDocumentCount: number,
      relationalDocumentList: RelationalDocument[],
      warehouseStatus: string,
      warehouseStatusCode: string,
      paperFlg: ContractFlag,
      electronicFlg: ContractFlag,
      registUser: string,
      registDate: string,
      updateUser: string,
      updateDate: string,
      barcodePrinting: string,
      requiredTagInputFlg?: number,
      itemCode?: string,
      folderId: number,
      folderName: string,
      authorityList: ControlCodeItem[],
      cartAddedFlg: number
      searchResult?: string
      previousPdfCount: number
    }): Document => ({
      ccControlNumber: item.ccControlNumber,
      id: item.documentId,
      name: item.documentName,
      fileName: item.documentFileName,
      fileSize: item.documentFileSize,
      fileId: item.documentFileId,
      typeId: item.documentTypeId,
      typeName: item.documentTypeName,
      tagList: item.tagList.map((tag: { tagLabel: string, tagValue: string }) => ({ label: tag.tagLabel, value: tag.tagValue })),
      attachmentFileCount: item.attachmentFileCount ?? 0,
      relationalDocumentCount: item.relationalDocumentCount,
      relationalDocumentList: item.relationalDocumentList,
      warehouseStatus: item.warehouseStatus,
      warehouseStatusCode: item.warehouseStatusCode,
      paperFlg: item.paperFlg,
      electronicFlg: item.electronicFlg,
      registUser: item.registUser,
      registDate: Formatter.fromStringToDate(item.registDate),
      updateUser: item.updateUser,
      updateDate: Formatter.fromStringToDate(item.updateDate),
      barcodePrinting: item.barcodePrinting,
      requiredTagInputFlg: item.requiredTagInputFlg,
      itemCode: item.itemCode,
      folderId: item.folderId,
      folderName: item.folderName,
      authorityList: folder ? folder.authorityList : item.authorityList,
      cartAddedFlg: item.cartAddedFlg,
      searchResult: item.searchResult,
      previousPdfCount: item.previousPdfCount ?? 0,
    })),
    sharedToken: data.sharedToken,
  };
}

function convertToDocumentOcrPagination(data: any, folder?: Folder): DocumentOcrPagination {
  return {
    pageLimit: data.pageLimit,
    page: data.page,
    total: data.total,
    ocrHighlightWordList: data.ocrHighlightWordList ? data.ocrHighlightWordList.map((item: { highlightWord: string }) => item.highlightWord) : [],
    searchKeyword: data.searchKeyword,
    searchKeywordList: data.searchKeywordList,
    sharedToken: data.sharedToken,
    documents: data.documentList.map((item: {
      ccControlNumber: number,
      documentId: number,
      documentName: string,
      documentFileName: string,
      documentFileSize: number,
      documentFileId: number,
      documentTypeId: number,
      documentTypeName: string,
      tagList: { tagLabel: string, tagValue: string }[],
      attachmentFileCount: number,
      relationalDocumentCount: number,
      relationalDocumentList: RelationalDocument[],
      warehouseStatus: string,
      warehouseStatusCode: string,
      paperFlg: ContractFlag,
      electronicFlg: ContractFlag,
      registUser: string,
      registDate: string,
      updateUser: string,
      updateDate: string,
      barcodePrinting: string,
      requiredTagInputFlg?: number,
      itemCode?: string,
      folderId: number,
      folderName: string,
      authorityList: ControlCodeItem[],
      cartAddedFlg: number
      searchResult?: string
      previousPdfCount: number
    }): Document => ({
      ccControlNumber: item.ccControlNumber,
      id: item.documentId,
      name: item.documentName,
      fileName: item.documentFileName,
      fileSize: item.documentFileSize,
      fileId: item.documentFileId,
      typeId: item.documentTypeId,
      typeName: item.documentTypeName,
      tagList: item.tagList.map((tag: { tagLabel: string, tagValue: string }) => ({ label: tag.tagLabel, value: tag.tagValue })),
      attachmentFileCount: item.attachmentFileCount ?? 0,
      relationalDocumentCount: item.relationalDocumentCount,
      relationalDocumentList: item.relationalDocumentList,
      warehouseStatus: item.warehouseStatus,
      warehouseStatusCode: item.warehouseStatusCode,
      paperFlg: item.paperFlg,
      electronicFlg: item.electronicFlg,
      registUser: item.registUser,
      registDate: Formatter.fromStringToDate(item.registDate),
      updateUser: item.updateUser,
      updateDate: Formatter.fromStringToDate(item.updateDate),
      barcodePrinting: item.barcodePrinting,
      requiredTagInputFlg: item.requiredTagInputFlg,
      itemCode: item.itemCode,
      folderId: item.folderId,
      folderName: item.folderName,
      authorityList: folder ? folder.authorityList : item.authorityList,
      cartAddedFlg: item.cartAddedFlg,
      searchResult: item.searchResult,
      previousPdfCount: item.previousPdfCount ?? 0,
    })),
  };
}

export interface DocumentGetOcrDetailForm {
  documentId: number,
  versionNo?: number,
}

export interface OcrDetailResult {
  ocrText: string,
  leftUpX: number,
  leftUpY: number,
  rightUpX: number,
  rightUpY: number,
  leftUnderX: number,
  leftUnderY: number,
  rightUnderX: number,
  rightUnderY: number,
}

export interface OcrResult {
  pageNo: number,
  ocrDetailResultList: OcrDetailResult[],
}

export interface OcrDocument {
  documentName: string,
  ocrResultList: OcrResult[],
}

export type SearchKeyOrSharedKey =
  | { searchKey: string; sharedKey?: never }
  | { sharedKey: string; searchKey?: never };

export interface SearchResultFileDetail {
  fileName: string,
  fileSize: string,
  requestDatetime: string,
  accessLimitDatetime: string,
  searchResultNumber: number,
  requestUser: string,
}

export interface DocumentBatchPdfFileDetailForm {
  requestDatetime: string,
  accessLimitDatetime: string,
  searchResultNumber: number,
  pdfFileList: { fileName: string, fileSize: string, fileNumber: number }[],
  requestUser: string,
}

export type DownloadDocumentBatchPdfFileForm = {
  fileNumber: number,
  fileName: string,
} & SearchKeyOrSharedKey;

export const getRegisterRequestBody = async (form: DocumentRegisterForm) => {
  const requestBody = {
    documentName: form.documentName,
    documentFile: form.documentFile ? await FileUtils.toBase64(form.documentFile) : undefined,
    documentFileName: form.documentFileName,
    documentFileSize: form.documentFile?.size,
    documentClassification: form.documentClassification,
    documentTypeId: form.documentTypeId,
    documentTypeName: form.documentTypeName,
    paperFlg: form.paperFlg,
    electronicFlg: form.electronicFlg,
    barcodePrinting: form.barcodePrinting,
    folderId: form.folderId,
    memo: form.memo,
    tagList: form.tagList ? form.tagList.map((tag) => ({
      tagLabel: tag.tagLabel,
      format: tag.format,
      formatCheck: tag.formatCheck ?? 0,
      value: TagFormat.normalizeTagValue(tag.format, tag.value),
    })) : undefined,
    relationalDocumentList: form.relationalDocumentList ? form.relationalDocumentList.map((relationalDocument) => ({
      relationalDocumentId: relationalDocument.id,
    })) : undefined,
    attachmentFileList: form.attachmentFileList ? await Promise.all(
      form.attachmentFileList
        .filter((attachment) => attachment.attachmentFile)
        .map(async (attachment) => ({
          attachmentFile: attachment.attachmentFile ? await FileUtils.toBase64(attachment.attachmentFile) : undefined,
          attachmentFileName: attachment.attachmentFileName,
          attachmentFileSize: attachment.attachmentFile?.size,
        })),
    ) : undefined,
  };

  return requestBody;
};

export const getUpdateRequestBody = async (form: DocumentUpdateForm) => {
  const payload: any = {
    documentId: form.id,
    documentName: form.name,
    documentFileSize: form.fileSize,
    documentFileId: form.fileId ?? undefined,
    documentClassification: form.classification,
    documentTypeId: form.typeId,
    documentTypeName: form.typeName,
    paperFlg: form.paperFlg,
    electronicFlg: form.electronicFlg,
    barcodePrinting: form.barcodePrinting,
    memo: form.memo,
    tagList: form.tagList ? form.tagList.map((tag) => ({
      tagLabel: tag.tagLabel || undefined,
      format: tag.format || undefined,
      formatCheck: tag.formatCheck ?? 0,
      value: TagFormat.normalizeTagValue(tag.format, tag.value) || undefined,
    })) : undefined,
    relationalDocumentList: form.relationalDocumentList ? form.relationalDocumentList.map((relationalDocument) => ({
      relationalDocumentId: Number(relationalDocument.id),
    })) : undefined,
    attachmentFileList: form.attachmentFileList ? await Promise.all(form.attachmentFileList.map(async (attachment) => ({
      attachmentFile: attachment.file ? await FileUtils.toBase64(attachment.file) : undefined,
      attachmentFileName: attachment.fileName,
      attachmentFileSize: attachment.fileSize,
      attachmentFileId: attachment.id || undefined,
    }))) : undefined,
  };

  if (form.fileId) {
    delete payload.documentFile;
    delete payload.documentFileName;
    delete payload.documentFileSize;
  } else if (form.file) {
    payload.documentFile = await FileUtils.toBase64(form.file);
    payload.documentFileName = form.fileName;
    delete payload.documentFileId;
  } else {
    payload.documentFileName = form.fileName;
    payload.documentFile = '';
  }

  return payload;
};

export default {
  /**
   * PDF文書の詳細検索を行います。
   *
   * @param form - PDF文書詳細検索フォーム
   * @returns - PDF文書詳細検索結果のページネーション
   */
  async searchPdfDetail(form: DocumentOcrSearchDetailForm): Promise<DocumentOcrPagination> {
    const res = await axios.post(
      '/Documents/searchPdfDetail',
      {
        ccControlNumber: Number(form.ccControlNumber),
        pdfSimpleString: form.pdfSimpleString,
        documentName: form.name,
        documentFileName: form.fileName,
        barcodePrinting: form.barcodePrinting,
        itemCode: form.itemCode,
        documentTypeId: form.documentTypeId,
        registDate_From: form.registDate_From ? Formatter.fromDateTimeToString(form.registDate_From, Formatter.defaultFullDateFormat) : undefined,
        registDate_To: form.registDate_To ? Formatter.fromDateTimeToString(form.registDate_To, Formatter.defaultFullDateFormat) : undefined,
        updateDate_From: form.updateDate_From ? Formatter.fromDateTimeToString(form.updateDate_From, Formatter.defaultFullDateFormat) : undefined,
        updateDate_To: form.updateDate_To ? Formatter.fromDateTimeToString(form.updateDate_To, Formatter.defaultFullDateFormat) : undefined,
        warehouseStatus: form.warehouseStatus,
        includePdf: form.includePdf,
        includeAttachment: form.includeAttachment,
        electronicContract: form.electronicContract,
        existPaper: form.existPaper,
        memo: form.memo,
        ccControlNumberList: form.ccControlNumberList ? form.ccControlNumberList.map((ccControlNumber) => ({
          ccControlNumber: ccControlNumber.ccControlNumber,
        })) : undefined,
        tagQuery: form.tagQuery,
        searchRange: form.searchRange?.toString(),
        page: form.page,
        sortList: form.sortList ? form.sortList.map((sort) => ({
          item: sort.item,
          order: sort.order,
        })) : undefined,
        bulkTagChange: form.bulkTagChange,
      },
    );
    const { data } = res;
    return convertToDocumentOcrPagination(data);
  },

  /**
   * 文書の詳細情報を取得します。
   *
   * @param form - 文書詳細情報取得フォーム
   * @returns - 文書詳細情報
   */
  async getDetail(form: DocumentGetDetailForm): Promise<DocumentDetail> {
    const res = await axios.get('/Documents/getDetail', { params: { documentId: form.id } });
    const data = res.data as {
      ccControlNumber: number,
      documentId: number,
      documentList: {
        documentDataId: number,
        versionNo: number,
        documentName: string,
        documentFileName: string,
        documentFileSize: number,
        documentFileOcrFlg: number,
        documentFileId: number,
        documentClassification: number,
        documentTypeId: number,
        documentTypeName: string,
        memo: string,
        attachmentFileList: {
          attachmentFileId: number,
          attachmentFileName: string,
        }[],
        tagList: {
          tagLabel: string,
          format: number,
          formatCheck?: number,
          value: string,
          required: number,
        }[],
        relationalDocumentList: {
          ccControlNumber: number,
          documentId: number,
          documentName: string,
          documentType: string,
          duplicateTagList: {
            tagLabel: string,
            value: string,
          }[]
        }[],
      }[],
      paperFlg: ContractFlag,
      electronicFlg: ContractFlag,
      barcodePrinting: string,
      barcode: string,
      warehouseStatus: string,
      warehouseStatusCode: string,
      itemCode: string,
      scheduledDate: string | null,
      barcodeChangeable: number,
      requestHistory: {
        requestDate: string,
        requestType: string,
        requestUser: string,
      }[],
      versionUpdateHistoryList: {
        logId: number | '',
        registDate: string,
        requestUser: string,
        updateType: string,
      }[],
      folder: {
        folderId: number,
        folderName: string,
        authorityList: [
          {
            controlCode: string
          },
        ],
        reservedFlg: boolean,
      },
      registUser: string,
      registDate: string,
      updateUser: string,
      updateDate: string,
    };
    return {
      ccControlNumber: data.ccControlNumber,
      id: data.documentId,
      paperFlg: data.paperFlg,
      electronicFlg: data.electronicFlg,
      barcodePrinting: data.barcodePrinting,
      barcode: data.barcode,
      warehouseStatus: data.warehouseStatus,
      warehouseStatusCode: data.warehouseStatusCode,
      itemCode: data.itemCode,
      scheduledDate: data.scheduledDate ? Formatter.fromStringToDate(data.scheduledDate) : null,
      barcodeChangeable: data.barcodeChangeable,
      documents: data.documentList.map((document) => ({
        dataId: document.documentDataId,
        versionNo: document.versionNo,
        name: document.documentName,
        fileName: document.documentFileName,
        fileSize: document.documentFileSize,
        documentFileOcrFlg: !!document.documentFileOcrFlg,
        fileId: document.documentFileId,
        classification: document.documentClassification,
        typeId: document.documentTypeId,
        typeName: document.documentTypeName,
        memo: document.memo,
        tags: document.tagList.map((tag) => ({
          tagLabel: tag.tagLabel,
          format: tag.format,
          formatCheck: tag.formatCheck,
          value: tag.value,
          required: !!tag.required,
        })),
        relationalDocuments: document.relationalDocumentList.map((relationalDocument) => ({
          ccControlNumber: relationalDocument.ccControlNumber,
          id: relationalDocument.documentId,
          name: relationalDocument.documentName,
          type: relationalDocument.documentType,
          duplicateTags: relationalDocument.duplicateTagList.map((duplicateTag) => ({
            label: duplicateTag.tagLabel,
            value: duplicateTag.value,
          })),
        })),
        attachmentFileList: document.attachmentFileList.map((attachment) => ({
          id: attachment.attachmentFileId,
          fileName: attachment.attachmentFileName,
        })),
      })),
      requestHistory: data.requestHistory.map((history) => ({
        date: history.requestDate,
        type: history.requestType,
        user: history.requestUser,
      })),
      versionUpdateHistory: data.versionUpdateHistoryList.map((versionUpdateHistory) => ({
        logId: versionUpdateHistory.logId === '' ? null : versionUpdateHistory.logId,
        registDate: versionUpdateHistory.registDate,
        requestUser: versionUpdateHistory.requestUser,
        updateType: versionUpdateHistory.updateType,
      })),
      folder: {
        id: data.folder.folderId,
        name: data.folder.folderName,
        reservedFlg: data.folder.reservedFlg,
        authorityList: data.folder.authorityList.map((authority) => ({
          controlCode: authority.controlCode,
        })),
        sharedToken: '',
      },
      registUser: data.registUser,
      registDate: Formatter.fromStringToDate(data.registDate),
      updateUser: data.updateUser,
      updateDate: Formatter.fromStringToDate(data.updateDate),
    };
  },

  /**
   * 文書の詳細検索を行います。
   *
   * @param form - 文書詳細検索フォーム
   * @returns - 文書詳細検索結果のページネーション
   */
  async searchDetail(form: DocumentSearchDetailForm): Promise<DocumentPagination> {
    const res = await axios.post(
      '/Documents/searchDetail',
      {
        ccControlNumber: Number(form.ccControlNumber),
        simpleString: form.simpleString,
        documentName: form.name,
        documentFileName: form.fileName,
        barcodePrinting: form.barcodePrinting,
        itemCode: form.itemCode,
        documentTypeId: form.documentTypeId,
        registDate_From: form.registDate_From ? Formatter.fromDateTimeToString(form.registDate_From, Formatter.defaultFullDateFormat) : undefined,
        registDate_To: form.registDate_To ? Formatter.fromDateTimeToString(form.registDate_To, Formatter.defaultFullDateFormat) : undefined,
        updateDate_From: form.updateDate_From ? Formatter.fromDateTimeToString(form.updateDate_From, Formatter.defaultFullDateFormat) : undefined,
        updateDate_To: form.updateDate_To ? Formatter.fromDateTimeToString(form.updateDate_To, Formatter.defaultFullDateFormat) : undefined,
        warehouseStatus: form.warehouseStatus,
        includePdf: form.includePdf,
        includeAttachment: form.includeAttachment,
        electronicContract: form.electronicContract,
        existPaper: form.existPaper,
        memo: form.memo,
        ccControlNumberList: form.ccControlNumberList ? form.ccControlNumberList.map((ccControlNumber) => ({
          ccControlNumber: ccControlNumber.ccControlNumber,
        })) : undefined,
        tagQuery: form.tagQuery,
        searchRange: form.searchRange?.toString(),
        page: form.page,
        sortList: form.sortList ? form.sortList.map((sort) => ({
          item: sort.item,
          order: sort.order,
        })) : undefined,
        bulkTagChange: form.bulkTagChange,
      },
    );
    const { data } = res;
    return convertToPagination(data);
  },

  /**
   * 文書フォルダの検索を行います。
   *
   * @param form - 文書フォルダ検索フォーム
   * @returns - 文書フォルダ検索結果のページネーション
   */
  async searchFolder(form: DocumentSearchFolderForm): Promise<DocumentPagination> {
    const res = await axios.post('/Documents/searchFolder', { folderId: form.folder.id, page: form.page, sortList: form.sortList });
    const { data } = res;
    return convertToPagination(data, form.folder);
  },

  /**
   * 文書の登録を行います。
   *
   * @param form - 文書登録フォーム
   * @returns - void
   */
  // eslint-disable-next-line consistent-return
  async register(form: DocumentRegisterForm): Promise<void> {
    const requestBody = await getRegisterRequestBody(form);

    if (FileUtils.isTooLarge(requestBody)) {
      // eslint-disable-next-line no-param-reassign
      form.isFormBodySizeTooLarge = true;
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        status: 'rejected' as const,
        reason: {
          message: ERROR_MESSAGES.FILE_SIZE_LIMIT_ERROR_MESSAGE,
        },
      });
    }

    if (form.documentFile) {
      const documentFileError = await FileUtils.validateDocumentFile(form.documentFile);
      if (documentFileError) {
        // eslint-disable-next-line no-param-reassign
        form.documentFileError = documentFileError;
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          status: 'rejected' as const,
          reason: {
            message: documentFileError,
          },
        });
      }
    }

    await axios.post('/Documents/register', requestBody);
  },

  /**
   * 文書ファイルのダウンロードを行います。
   *
   * @param form - ダウンロードファイルフォーム
   * @returns - ダウンロードしたファイル
   */
  async downloadFile(form: DownloadFileForm): Promise<File> {
    const res = await axios.post('/Documents/downloadFile', { documentDataId: form.documentDataId });
    return FileUtils.fromBase64(`${res.data.documentFile}`, form.fileName || 'file', { type: 'application/pdf' });
  },

  /**
   * 文書の添付ファイルをダウンロードします。
   *
   * @param form - ダウンロード添付ファイルフォーム
   * @returns - ダウンロードした添付ファイル
   */
  async downloadAttachment(form: DownloadAttachmentForm): Promise<File> {
    const res = await axios.post('/Documents/downloadAttachment', { attachmentFileId: form.attachmentId });
    return FileUtils.fromBase64(`${res.data.attachmentFile}`, form.fileName || 'file', { type: 'application/pdf' });
  },

  /**
   * 文書PDF一括ダウンロードを行います。
   *
   * @param form - ダウンロードファイルフォーム
   * @returns - サーバーのレスポンス
   */
  async batchPdfDownload(form: BatchPdfDownloadForm) {
    const res = await axios.post(
      '/Documents/getSearchResultPdfFiles',
      form,
    );

    return res.data;
  },

  /**
   * 文書の検索結果ファイルをダウンロードします。
   *
   * @param searchKey - 検索キー
   * @returns - ダウンロードしたファイル
   */
  async downloadSearchResultDocuments(form: SearchKeyOrSharedKey): Promise<File> {
    const res = await axios.post('/Documents/downloadSearchResultFile', form);
    return FileUtils.fromBase64(`${res.data.downloadSearchResultFile}`, res.data.downloadSearchResultFileName);
  },

  /**
   * 検索結果ファイル詳細情報を取得します。
   *
   * @param searchKey - 検索キー
   * @returns - 検索結果ファイル詳細情報
   */
  async getSearchResultFileDetail(params: SearchKeyOrSharedKey) {
    const res = await axios.get<SearchResultFileDetail>('/Documents/getSearchResultFileDetail', { params });
    return res.data;
  },

  /**
   * 文書一括PDFファイル詳細情報を取得します。
   *
   * @param form - ダウンロードファイルフォーム
   * @returns - 検索結果ファイル詳細情報
   */
  async getSearchResultPdfFileDetail(params: SearchKeyOrSharedKey) {
    const res = await axios.get<DocumentBatchPdfFileDetailForm>('/Documents/getSearchResultPdfFileDetail', { params });
    return res.data;
  },

  /**
   * 文書の検索結果ファイルをダウンロードします。
   *
   * @param searchKey - 検索キー
   * @returns - ダウンロードしたファイル
   */
  async downloadDocumentBatchPdfFile(form: DownloadDocumentBatchPdfFileForm): Promise<File> {
    const { fileName, ...rest } = form;
    const res = await axios.post('/Documents/downloadSearchResultPdfFiles', rest, { responseType: 'blob' });
    return new File([res.data], fileName);
  },

  /**
   * 文書の更新を行います。
   *
   * @param form - 文書更新フォーム
   * @returns - void
   */
  async update(form: DocumentUpdateForm): Promise<void> {
    const payload = await getUpdateRequestBody(form);

    if (FileUtils.isTooLarge(payload)) {
      const error = new Error(ERROR_MESSAGES.FILE_SIZE_LIMIT_ERROR_MESSAGE);
      (error as ApiError).isFileTooLarge = true;
      throw error;
    }

    await axios.post('/Documents/update', payload);
  },

  /**
   * 文書のリビジョンを行います。
   *
   * @param form - 文書リビジョンフォーム
   * @returns - void
   */
  async revision(form: DocumentRevisionForm): Promise<void> {
    const payload: any = {
      documentId: form.id,
      documentName: form.name,
      documentFile: form.file ? await FileUtils.toBase64(form.file) : undefined,
      documentFileName: form.fileName ?? '',
      documentFileSize: form.fileSize,
      documentFileId: form.fileId ?? undefined,
      documentClassification: form.classification,
      documentTypeId: form.typeId,
      documentTypeName: form.typeName,
      paperFlg: form.paperFlg,
      electronicFlg: form.electronicFlg,
      barcodePrinting: form.barcodePrinting,
      memo: form.memo,
      tagList: form.tagList ? form.tagList.map((tag) => ({
        tagLabel: tag.tagLabel || undefined,
        format: tag.format || undefined,
        formatCheck: tag.formatCheck ?? 0,
        value: TagFormat.normalizeTagValue(tag.format, tag.value) || undefined,
      })) : undefined,
      relationalDocumentList: form.relationalDocumentList ? form.relationalDocumentList.map((relationalDocument) => ({
        relationalDocumentId: Number(relationalDocument.id),
      })) : undefined,
      attachmentFileList: form.attachmentFileList ? await Promise.all(form.attachmentFileList.map(async (attachment) => ({
        attachmentFileId: attachment.id,
        attachmentFile: attachment.file ? await FileUtils.toBase64(attachment.file) : undefined,
        attachmentFileName: attachment.fileName,
        attachmentFileSize: attachment.fileSize,
      }))) : undefined,
    };

    if (FileUtils.isTooLarge(payload)) {
      const error = new Error(ERROR_MESSAGES.FILE_SIZE_LIMIT_ERROR_MESSAGE);
      (error as ApiError).isFileTooLarge = true;
      throw error;
    }

    await axios.post('/Documents/revision', payload);
  },

  /**
   * 文書の削除を行います。
   *
   * @param form - 文書削除フォーム
   * @returns - void
   */
  async delete(form: DocumentDeleteForm): Promise<void> {
    await axios.post('/Documents/delete', {
      documentId: form.id,
    });
  },

  /**
   * 文書のリビジョンの削除を行います。
   *
   * @param form - 文書リビジョン削除フォーム
   * @returns - void
   */
  async deleteRevision(form: DocumentDeleteRevisionForm): Promise<void> {
    await axios.post('/Documents/deleteRevision', {
      documentId: form.id,
      version: form.versionId,
    });
  },

  /**
   * 文書のフォルダの移動を行います。
   *
   * @param form - 文書フォルダ移動フォーム
   * @returns - void
   */
  async moveFolder(form: MoveDocumentFolderForm): Promise<void> {
    const res = await axios.post('/Documents/moveFolder', form);
    const { data } = res;
    return data;
  },

  /**
   * OCRデータを取得します。
   *
   * @param form - OCRデータ取得用フォーム
   * @returns - 取得したOCRデータ
   */
  async getOcrDetail(form: DocumentGetOcrDetailForm): Promise<OcrDocument> {
    const res = await axios.get<OcrDocument>('/Documents/getOcrDetail', { params: form });

    return res.data;
  },

  async getSearchResultFile(form: DocumentSearchDetailForm): Promise<void> {
    const res = await axios.post(
      '/Documents/getSearchResultFile',
      {
        ccControlNumber: Number(form.ccControlNumber) || undefined,
        searchText: form.pdfSimpleString ? form.pdfSimpleString : form.simpleString,
        documentName: form.name || undefined,
        documentFileName: form.fileName || undefined,
        barcodePrinting: form.barcodePrinting,
        itemCode: form.itemCode,
        documentTypeId: form.documentTypeId,
        registDate_From: form.registDate_From ? Formatter.fromDateTimeToString(form.registDate_From, Formatter.defaultFullDateFormat) : undefined,
        registDate_To: form.registDate_To ? Formatter.fromDateTimeToString(form.registDate_To, Formatter.defaultFullDateFormat) : undefined,
        updateDate_From: form.updateDate_From ? Formatter.fromDateTimeToString(form.updateDate_From, Formatter.defaultFullDateFormat) : undefined,
        updateDate_To: form.updateDate_To ? Formatter.fromDateTimeToString(form.updateDate_To, Formatter.defaultFullDateFormat) : undefined,
        electronicContract: form.electronicContract,
        existPaper: form.existPaper,
        memo: form.memo || undefined,
        warehouseStatus: form.warehouseStatus,
        ccControlNumberList: form.ccControlNumberList ? form.ccControlNumberList.map((ccControlNumber) => ({
          ccControlNumber: ccControlNumber.ccControlNumber,
        })) : undefined,
        tagQuery: form.tagQuery,
        searchRange: form.searchRange?.toString(),
        pdfSearchFlg: form.pdfSimpleString ? 1 : 0,
      },
    );
    const { data } = res;
    return data;
  },
};
